import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { articlesApi } from '@texas/api/endpoints/articlesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { SpinnerWithLabel } from '../shared/SpinnerWithLabel';
import { Text } from '@chakra-ui/react';

interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

export function useItemCodeValidator(itemCode: string) {
  const { t } = useTranslation();
  const [result, setResult] = useState<ValidationResult>({
    isValid: true,
  });
  const { request, loading } = useApiRequest(articlesApi.validateItemCodes);

  const validateItemCodeDebounce = useDebouncedCallback(async () => {
    await validateItemCode();
  }, 300);

  const validateItemCode = useCallback(async () => {
    if (!itemCode) {
      setResult({ isValid: false });
      return;
    }

    const response = await request([itemCode]);

    if (!response.hasError && response.data[itemCode]) {
      setResult({
        isValid: response.data[itemCode]!.isValid,
        errorMessage: response.data[itemCode]!.errorMessage ?? undefined,
      });
    } else {
      setResult({
        isValid: false,
        errorMessage: t('vdc.invalidItemCode'),
      });
    }
  }, [itemCode, request, t]);

  const component = useMemo(() => {
    if (loading) {
      return <SpinnerWithLabel size="sm" label={t('vdc.validating')} />;
    }
    if (!result.isValid) {
      return (
        <Text color="red.300">
          {result.errorMessage ?? t('vdc.invalidItemCode')}
        </Text>
      );
    }
    return null;
  }, [loading, result, t]);

  useEffect(() => {
    validateItemCodeDebounce();
  }, [itemCode, validateItemCodeDebounce]);

  return { result, component, validateItemCode };
}
